import { useMemo, useEffect, useState } from 'preact/hooks'

import { SearchspringSearchProvider } from '../fetchers/search'
import { useOption } from '../hooks/useOption'

import { scrollHistoryManager } from '@/components/helpers/scroll-history'
import { useInfiniteScrollResults } from '../hooks/useInfiniteScrollResults'
import { InfiniteScrollNext, InfiniteScrollPrevious } from './InfiniteScroll'
import { useScrollPositionKeeper } from '../hooks/useScrollPositionKeeper'
import { useScrollCache } from '../hooks/useScrollCache'
import { useFilterScroll } from '../hooks/useFilterScroll'

import { ResultsCard } from './ResultCard'
import { ResultCount } from './ResultCount'
import { FilterSort } from './FilterSort'
import { FilterBarMobile } from './FilterBarMobile'

import { FilterSidebar } from './FilterSidebar'

import useOfflineWishlist from '@/components/hooks/useOfflineWishlist'
import { useWishlist } from '@/components/hooks/useWishlist'

function SearchGrid() {
  history.scrollRestoration = 'manual'
  const { allResults, gridChange, isLoading } = useInfiniteScrollResults()
  const { setEntry } = useScrollPositionKeeper(gridChange)
  const [firstLoad, setFirstLoad] = useState(true)
  const { setScrollElement } = useFilterScroll()
  const query = useOption('query', { defaultValue: '', syncWithURL: false })

  const { wishlist, getKeys, ...callbacks } = useOfflineWishlist()
  const { wishlists: omneoWishlists } = useWishlist()

  const wishlistItems = useMemo(() => {
    return getKeys()
  }, [wishlist])

  useEffect(() => {
    if (!isLoading) setFirstLoad(false)
  }, [isLoading])

  useScrollCache([allResults, firstLoad])

  if (!isLoading && allResults.length === 0) {
    return (
      <div className="flex flex-col" data-no-results>
        <div class="flex flex-col gap-6 bg-primary-sale/75 px-4 pb-6 md:gap-8 md:px-10">
          <h1 class="h1 md:basis-1/2">No results for "{query}"</h1>
          <div class="flex flex-col gap-2">
            <p class="h4">Sorry we couldn't find what you were looking for...</p>
            <p>Check your spelling, try another search or browse our popular products and collections below.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      <div class="flex justify-between gap-4 bg-secondary-paleblue px-4 pb-6 md:px-10">
        <h1 class="h1 md:basis-1/2">
          Showing {allResults?.pagination?.totalResults} results for "{query}"
        </h1>
        <div className="items-center hidden gap-6 md:flex">
          <ResultCount className="pl-2 whitespace-nowrap text-neutrals-secondary-grey" />
          <FilterSort />
        </div>
      </div>
      <FilterBarMobile />
      <div ref={setScrollElement} className="grid grid-cols-1 py-6 md:grid-cols-4 md:gap-10 md:px-10 md:py-10">
        <div className="sticky hidden col-span-1 top-48 md:block">
          <FilterSidebar />
        </div>

        <div
          className="grid grid-cols-2 col-span-1 gap-x-2 gap-y-6 md:col-span-3 md:grid-cols-3 md:gap-x-4 h-fit"
          ref={setEntry}
        >
          <InfiniteScrollPrevious className={'col-span-2 md:col-span-3'} />
          {allResults?.map((item, index) => {
            return 'value' in item ? (
              <div
                // TODO: when ready for multi device assets, swap classes here
                // className="relative col-span-full my-4 aspect-[16/9] md:col-span-1 md:my-0 md:aspect-9/16"
                className="relative col-span-full aspect-square md:col-span-1"
              >
                <div
                  dangerouslySetInnerHTML={{ __html: item.value }}
                  // TODO: when ready for multi device assets, swap classes here
                  // className="absolute overflow-hidden -inset-4 md:static"
                  className="absolute inset-0"
                  key={item.value}
                />
              </div>
            ) : (
              <ResultsCard
                wishlist={{
                  omneoWishlists,
                  offlineWishlistItems: wishlistItems,
                  wsCallbacks: callbacks,
                  ...(item.handle.includes('gift-card') && { type: 'none' })

                }}
                pageType="search"
                query={query}
                result={item}
                index={index}
                key={item.handle}
                className="wk-wishlist-button-target"
              />
            )
          })}
        </div>
      </div>
      {allResults.length > 0 && !firstLoad && (
        <div className="flex justify-center px-4 py-6">
          <InfiniteScrollNext />
        </div>
      )}
    </div>
  )
}

export function WrappedSearchGrid() {
  scrollHistoryManager.updateHistoryState()
  const urlParams = new URLSearchParams(window.location.search)
  const queryParam = urlParams.get('query')

  return (
    <SearchspringSearchProvider query={queryParam}>
      <SearchGrid />
    </SearchspringSearchProvider>
  )
}
